//@ts-nocheck
import React, { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Chip,
} from '@mui/material';
import { green, grey, blue, purple } from '@mui/material/colors';
import { getConnectorRequests, ConnectorRequest } from '../../Services/quantiAI';

// #region Helpers
const getStatusColor = (status: string): string => {
	switch (status) {
		case 'active':
			return green[500];
		case 'inactive':
			return grey[500];
		default:
			return grey[500];
	}
};

const getTypeColor = (type: string): 'primary' | 'secondary' | 'default' => {
	switch (type) {
		case 'prebuild':
			return 'primary';
		case 'custom':
			return 'secondary';
		default:
			return 'default';
	}
};
// #endregion

// #region ConnectorRequestList
export const ConnectorRequestList = () => {
	const record = useRecordContext();
	const [requests, setRequests] = useState<ConnectorRequest[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!record?.projectFolderId || !record?.sku || !record?.accountId) {
			setIsLoading(false);
			return;
		}

		const loadRequests = async () => {
			try {
				setIsLoading(true);
				setError(null);
				const response = await getConnectorRequests(
					record.projectFolderId,
					record.sku,
					record.accountId
				);
				setRequests(response.requests || []);
			} catch (err: any) {
				console.error('Error loading connector requests:', err);
				setError(err.message || 'Erreur lors du chargement');
			} finally {
				setIsLoading(false);
			}
		};

		loadRequests();
	}, [record?.projectFolderId, record?.sku, record?.accountId]);

	if (!record) {
		return (
			<Card>
				<CardContent>
					<Typography>Chargement...</Typography>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card>
				<CardContent>
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress size={24} />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Requests configurees
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{error}
					</Typography>
				</CardContent>
			</Card>
		);
	}

	const activeRequests = requests.filter((r) => r.status === 'active');
	const inactiveRequests = requests.filter((r) => r.status === 'inactive');

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Requests configurees ({requests.length})
				</Typography>
				{requests.length > 0 ? (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Request ID</TableCell>
									<TableCell>Nom</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Status</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{requests.map((request) => (
									<TableRow key={request.id} hover>
										<TableCell>
											<Typography
												variant="body2"
												sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
											>
												{request.request_id}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2">{request.request_name}</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={request.request_type}
												size="small"
												variant="outlined"
												color={getTypeColor(request.request_type)}
											/>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												sx={{
													color: getStatusColor(request.status),
													fontWeight: 500,
												}}
											>
												{request.status}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				) : (
					<Typography variant="body2" color="text.secondary">
						Aucune request configuree pour ce connecteur
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion
